import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ModelType } from "~/lib/constants";
import { getModelActionsUrl } from "~/lib/utils";

import { SuggestionItem, SuggestionProps } from ".";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../../command";
import { Icon } from "../../icon";
import { filterChildren, tokenizeQuery } from "./filter";
import { applyRecency, loadRecents, recordRecent } from "./recents";

/**
 * Rows shown per category before "Show all". Kept deliberately low: categories are
 * expanded by default (an `@` picker is an insertion flow, not a browse flow — you
 * should see real records without a click), so the cap is what keeps the popup
 * scannable rather than a wall.
 */
const PREVIEW_LIMIT = 3;

/** Once searching, show more per category since the rows are ranked and relevant. */
const SEARCH_LIMIT = 5;

/** Stable, unique cmdk value for a row. */
const itemValue = (child: SuggestionItem | undefined, index: number) =>
  child ? `${index}-${child.id}` : "";

export interface MentionListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface MentionListProps {
  items: SuggestionProps[];
  command: (item: SuggestionItem) => void;
  /** The text typed after the trigger char. Drives browse-vs-search mode. */
  query?: string;
  /** Supplied by TipTap's ReactRenderer; used to hand focus back to the document. */
  editor?: { commands: { focus: () => void } };
  /** Scopes the recently-used list so it never crosses users on a shared browser. */
  userId?: string;
}

const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  ({ items, command, query = "", editor, userId }, ref) => {
    // Categories the user has explicitly *closed*. Everything is open by default,
    // so this tracks the exception rather than the rule.
    const [closedHeadings, setClosedHeadings] = useState<Set<string>>(new Set());
    const [showAll, setShowAll] = useState<Set<string>>(new Set());
    // Per-category "search within", keyed by heading so several can be open at once.
    const [categorySearch, setCategorySearch] = useState<Record<string, string>>(
      {},
    );
    const [searchOpen, setSearchOpen] = useState<Set<string>>(new Set());
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const isSearching = tokenizeQuery(query).length > 0;

    // Read once per mount — recents only change when this popup inserts something,
    // and re-reading storage on every keystroke would be wasteful.
    const recents = useMemo(() => loadRecents(userId), [userId]);

    /**
     * One entry per category with its resolved open state and the rows to render.
     * Categories are open by default; searching force-opens whatever still matches
     * so results are never hidden behind a section the user happened to close.
     */
    const sections = useMemo(
      () =>
        items.map((group) => {
          const search = categorySearch[group.heading] ?? "";
          const matched = filterChildren(group.children, group.heading, search);
          // With no query the order is otherwise arbitrary (API insertion order),
          // so float recently-used records to the top to make the preview useful.
          const ordered =
            isSearching || search ? matched : applyRecency(matched, recents);
          const isOpen = isSearching || !closedHeadings.has(group.heading);
          const limit = isSearching ? SEARCH_LIMIT : PREVIEW_LIMIT;
          const rows = !isOpen
            ? []
            : showAll.has(group.heading)
              ? ordered
              : ordered.slice(0, limit);
          return { group, isOpen, matched: ordered, rows };
        }),
      [items, categorySearch, isSearching, closedHeadings, showAll, recents],
    );

    /** The rows the keyboard walks, in render order. */
    const visibleItems = useMemo(
      () => sections.flatMap((section) => section.rows),
      [sections],
    );

    // Keep the cursor in range whenever the rendered set changes.
    useEffect(() => {
      setSelectedIndex(0);
    }, [items, closedHeadings, showAll, categorySearch, isSearching]);

    const toggleHeading = (heading: string) => {
      setClosedHeadings((prev) => {
        const next = new Set(prev);
        if (next.has(heading)) {
          next.delete(heading);
        } else {
          next.add(heading);
        }
        return next;
      });
      // Closing a category should also put its extras and its search box away.
      setShowAll((prev) => {
        const next = new Set(prev);
        next.delete(heading);
        return next;
      });
      setSearchOpen((prev) => {
        const next = new Set(prev);
        next.delete(heading);
        return next;
      });
      setCategorySearch((prev) => ({ ...prev, [heading]: "" }));
    };

    const selectItem = (child: SuggestionItem | undefined) => {
      if (!child) return;
      recordRecent(userId, child);
      if (child.type === "item") {
        command({
          ...child,
          label: `${child?.reference}-${child.label}`,
          id: getModelActionsUrl(
            child.modelType as ModelType,
            child.id as string,
          ),
        });
      } else {
        command(child);
      }
    };

    const closeSearch = (heading: string) => {
      setSearchOpen((prev) => {
        const next = new Set(prev);
        next.delete(heading);
        return next;
      });
      setCategorySearch((prev) => ({ ...prev, [heading]: "" }));
      editor?.commands.focus();
    };

    /**
     * Shared list-key handling, used both by TipTap (focus in the editor) and by
     * the in-category search inputs (focus in the popup). Returns true when the
     * key was consumed. Escape is deliberately NOT consumed — it closes the popup.
     */
    const handleListKey = (key: string): boolean => {
      if (visibleItems.length === 0) return false;
      if (key === "ArrowUp") {
        setSelectedIndex(
          (index) => (index + visibleItems.length - 1) % visibleItems.length,
        );
        return true;
      }
      if (key === "ArrowDown") {
        setSelectedIndex((index) => (index + 1) % visibleItems.length);
        return true;
      }
      if (key === "Enter" || key === "Tab") {
        selectItem(visibleItems[selectedIndex]);
        return true;
      }
      return false;
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => handleListKey(event.key),
    }));

    // Scroll the keyboard cursor into view.
    useEffect(() => {
      const node = listRef.current?.querySelector<HTMLElement>(
        `[data-mention-index="${selectedIndex}"]`,
      );
      node?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex]);

    const selectedValue = itemValue(visibleItems[selectedIndex], selectedIndex);

    let flatIndex = -1;

    return (
      <div
        className="suggestions z-50 !rounded-2xl bg-white"
        style={{ zIndex: 99 }}
        onMouseDown={(e) => {
          // Keep focus in the editor so the caret/range survives the click.
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Command
          className="rounded-2xl"
          // Filtering lives in filter.ts, not cmdk — cmdk has no CommandInput wired
          // up, so its internal search string is always empty and everything passes.
          shouldFilter={false}
          value={selectedValue}
          onValueChange={(value) => {
            const index = visibleItems.findIndex(
              (child, idx) => itemValue(child, idx) === value,
            );
            if (index !== -1) setSelectedIndex(index);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CommandList
            ref={listRef}
            className="max-h-[480px] p-2"
            data-testid="mention-list"
          >
            {items.length === 0 && (
              <CommandEmpty data-testid="mention-empty">
                No results found.
              </CommandEmpty>
            )}

            {sections.map(({ group, isOpen, matched, rows }, index) => (
              <Fragment key={group.heading}>
                <CommandGroup className="p-0">
                  {/* Header doubles as the accordion toggle. Siblings stay put. */}
                  <button
                    type="button"
                    data-testid={`mention-toggle-${group.heading}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left hover:bg-neutral-100"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleHeading(group.heading);
                    }}
                  >
                    <span className="font-Bold text-sm text-neutral-900">
                      {group.heading}{" "}
                      <span className="font-normal text-neutral-500">
                        ({group.children.length})
                      </span>
                    </span>
                    <Icon
                      name={isOpen ? "ChevronDown" : "ChevronRight"}
                      aria-label={
                        isOpen
                          ? `Collapse ${group.heading}`
                          : `Expand ${group.heading}`
                      }
                      className="size-4 shrink-0 text-neutral-900"
                    />
                  </button>

                  {isOpen && (
                    <div data-testid={`mention-section-${group.heading}`}>
                      {/* Opened from its icon so it only takes focus when asked for. */}
                      {searchOpen.has(group.heading) ? (
                        <div className="mx-2 mb-1 flex items-center gap-2 rounded-xl border border-neutral-200 px-2">
                          <Icon
                            name="Search"
                            className="size-4 shrink-0 text-neutral-500"
                          />
                          <input
                            autoFocus
                            type="text"
                            value={categorySearch[group.heading] ?? ""}
                            data-testid={`mention-category-search-${group.heading}`}
                            placeholder={`Search ${group.heading.toLowerCase()}`}
                            className="w-full bg-transparent py-2 text-sm outline-none"
                            onChange={(e) =>
                              setCategorySearch((prev) => ({
                                ...prev,
                                [group.heading]: e.target.value,
                              }))
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                e.preventDefault();
                                e.stopPropagation();
                                closeSearch(group.heading);
                                return;
                              }
                              // Focus is in the input, so TipTap never sees these.
                              if (
                                e.key === "ArrowUp" ||
                                e.key === "ArrowDown" ||
                                e.key === "Enter"
                              ) {
                                if (handleListKey(e.key)) e.preventDefault();
                              }
                              e.stopPropagation();
                            }}
                          />
                        </div>
                      ) : (
                        group.children.length > SEARCH_LIMIT && (
                          <button
                            type="button"
                            data-testid={`mention-search-toggle-${group.heading}`}
                            aria-label={`Search ${group.heading}`}
                            className="mx-2 mb-1 flex items-center gap-2 rounded-xl px-2 py-1 text-sm text-neutral-500 hover:text-neutral-900"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setSearchOpen((prev) =>
                                new Set(prev).add(group.heading),
                              );
                            }}
                          >
                            <Icon name="Search" className="size-4 shrink-0" />
                            Search {group.heading.toLowerCase()}
                          </button>
                        )
                      )}

                      {rows.length === 0 && (
                        <div
                          data-testid="mention-empty"
                          className="px-3 py-2 text-sm text-neutral-500"
                        >
                          No results found.
                        </div>
                      )}

                      {rows.map((child) => {
                        flatIndex += 1;
                        const Row =
                          child.type === "item" ? ItemComponent : UserComponent;
                        return (
                          <Row
                            key={`${child.id}-${flatIndex}`}
                            child={child}
                            flatIndex={flatIndex}
                            onSelect={selectItem}
                          />
                        );
                      })}

                      {matched.length > rows.length && (
                        <button
                          type="button"
                          data-testid={`mention-show-all-${group.heading}`}
                          className="w-full px-3 py-1 text-left text-sm text-neutral-500 hover:text-neutral-900"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowAll((prev) =>
                              new Set(prev).add(group.heading),
                            );
                          }}
                        >
                          Show all {matched.length}
                        </button>
                      )}
                    </div>
                  )}
                </CommandGroup>
                {index < sections.length - 1 && <CommandSeparator />}
              </Fragment>
            ))}
          </CommandList>
        </Command>
      </div>
    );
  },
);

MentionList.displayName = "MentionList";

export default MentionList;

const ItemComponent: React.FC<{
  child: SuggestionItem;
  flatIndex: number;
  onSelect: (child: SuggestionItem) => void;
}> = ({ child, flatIndex, onSelect }) => {
  return (
    <CommandItem
      value={itemValue(child, flatIndex)}
      data-mention-index={flatIndex}
      data-testid="mention-option"
      className="w-full cursor-pointer rounded-2xl hover:bg-neutral-200"
      onSelect={() => onSelect(child)}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(child);
      }}
    >
      <div className="min-w-0 flex-1">
        <span className="block truncate font-Bold text-sm">
          {child.reference}
        </span>
        <div className="flex min-w-0 items-center gap-1 text-sm text-neutral-500">
          <span className="min-w-0 flex-1 truncate capitalize">
            {child.label}
          </span>
          <div className="size-1 shrink-0 bg-neutral-500" />
          <span className="shrink-0">{child.modelType}</span>
        </div>
      </div>
    </CommandItem>
  );
};

const UserComponent: React.FC<{
  child: SuggestionItem;
  flatIndex: number;
  onSelect: (child: SuggestionItem) => void;
}> = ({ child, flatIndex, onSelect }) => {
  return (
    <CommandItem
      value={itemValue(child, flatIndex)}
      data-mention-index={flatIndex}
      data-testid="mention-option"
      className="flex w-full cursor-pointer items-center rounded-2xl hover:bg-neutral-200"
      onSelect={() => onSelect(child)}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(child);
      }}
    >
      <img
        src={child.image}
        alt={child.label}
        className="mr-3 size-10 shrink-0 rounded-full"
        onError={(e) => {
          e.currentTarget.src = `https://avatar.iran.liara.run/username?username=${child.label}`;
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-Bold text-sm">{child.label}</div>
        <div className="truncate text-sm text-neutral-500">{child.role}</div>
      </div>
    </CommandItem>
  );
};
